// The three videos offered when one ends (EndScreen.vue), picked in the browser from the video
// list and the speaker index (2026-10-14):
// 1. the speakers' other videos, newest first;
// 2. up to two other talks from the same year and session, at random (the other keynotes, two of
//    the other DNA talks);
// 3. then, at random, talks from the same session in the latest earlier year that has one (so a
//    new speaker's video always offers one talk from an earlier year).
// 4. any slot still empty (a session with few talks, e.g. Shorts): keynotes, the same way.
// A video with no session tag is treated as a keynote.
import {getVideoSpeakerKeys} from "@/composables/useSpeakerIndex";

const COUNT = 3;
const FALLBACK_TAG = "Keynote";

// every video: {hash, showcase, year, tag, speakerKeys}
function getVideos(showcases) {
  return Object.entries(showcases || {}).flatMap(([showcase, {videos}]) => {
    const year = Number(showcase.match(/\d{4}/)?.[0]);
    if (!year) return [];
    return Object.entries(videos || {}).map(([hash, video]) => {
      const speakerKeys = getVideoSpeakerKeys(video.name);
      const tag = video.tag === "Keynotes" ? FALLBACK_TAG : video.tag || ""; // 2011 has one
      return {hash, showcase, year, tag, speakerKeys};
    });
  });
}

function getShuffled(list) {
  return list.map((item) => [Math.random(), item]).sort(([a], [b]) => a - b).map(([, x]) => x);
}

function getSpeakerPicks(current, getSpeaker) {
  return current.speakerKeys.flatMap((key) => (getSpeaker(key)?.videos || []).map((v) => v.hash));
}

function getSessionPicks(current, tag, videos) {
  const isSame = (video) => video.year === current.year && video.tag === tag;
  const others = videos.filter((video) => isSame(video) && video.hash !== current.hash);
  return getShuffled(others).slice(0, COUNT - 1).map((video) => video.hash);
}

// earlier years with the session, latest first; each year's talks in random order
function getEarlierPicks(current, tag, videos) {
  const earlier = videos.filter((video) => video.tag === tag && video.year < current.year);
  const years = [...new Set(earlier.map((video) => video.year))].sort((a, b) => b - a);
  return years.flatMap((year) => {
    return getShuffled(earlier.filter((video) => video.year === year)).map((video) => video.hash);
  });
}

// [{hash, showcase}] for the video that ended: up to three, never itself, each once
export function getRecommendations(hash, showcases, getSpeaker) {
  const videos = getVideos(showcases);
  const current = videos.find((video) => video.hash === hash);
  if (!current) return [];
  const byHash = new Map(videos.map((video) => [video.hash, video]));
  const tag = current.tag || FALLBACK_TAG;
  const candidates = [
    ...getSpeakerPicks(current, getSpeaker),
    ...getSessionPicks(current, tag, videos),
    ...getEarlierPicks(current, tag, videos),
    ...getSessionPicks(current, FALLBACK_TAG, videos),
    ...getEarlierPicks(current, FALLBACK_TAG, videos)
  ];
  const picks = [...new Set(candidates)].filter((pick) => pick !== hash && byHash.has(pick));
  return picks.slice(0, COUNT).map((pick) => byHash.get(pick));
}
